import '../styles/App.css';
import profilePic from '../assets/profile.jpeg';
import { Link } from "react-router-dom";
import { useEffect } from 'react';
import { currentProjects } from '../data/now';

const TWITTER_LINK = 'https://twitter.com/OmniscientHrzn';
const GITHUB_LINK = 'https://github.com/omniscientHorizon';
const LINKEDIN = 'https://www.linkedin.com/in/coachkshitijagarwal/';
const BOOK_LINK = 'https://www.amazon.in/dp/9360060763?ref_=cm_sw_r_cp_ud_dp_T2PP464JPARY923WT7DF';

const STACK = [
  { label: 'languages', tags: ['Python', 'JavaScript', 'SQL', 'Solidity', 'C', 'Java', 'Swift'] },
  { label: 'ml / ai', tags: ['PyTorch', 'scikit-learn', 'CNNs', 'RNNs', 'LSTMs', 'Attention', 'SVMs', 'pandas', 'NumPy'] },
  { label: 'tools & infra', tags: ['Git', 'Linux', 'REST APIs', 'Deepgram', 'Exotel', 'Screen Time API', 'Google Calendar API'] },
  { label: 'web3', tags: ['Solidity', 'Hardhat', 'ethers.js', 'smart contracts'] },
  { label: 'frameworks', tags: ['React', 'Node.js'] },
];

export default function Home() {
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      e.preventDefault();
      const el = document.querySelector(a.getAttribute('href'));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="App">
      <div className="container">

        {/* NAV */}
        <div className="options">
          <a href="#journey">about</a>
          <a href="#projects">projects</a>
          <a href="#research">research</a>
          <Link to="/to-think">thoughts</Link>
          <a href="/resume.pdf" target="_blank" rel="noreferrer">resume</a>
        </div>

        {/* HERO */}
        <div className="intro" id="intro">
          <h1 style={{ display: 'none' }}>Kshitij Agarwal</h1>

          <img className="profilePic left" src={profilePic} alt="Kshitij Agarwal" />

          <p>
            <b className="hero-name">hi, i'm kshitij.</b>
            <br /><br />
            i build AI-powered tools and ship fast. currently: founding <b>Blue</b>, researching
            brain-computer interfaces at union college's HCI lab, and working on a senior thesis
            around zero-knowledge proofs for verifiable AI inference.
            <br /><br />
            i started in web3 — learned solidity in high school, shipped dApps at ethglobal
            hackathons, got called the "solidity wizard," and wrote a{' '}
            <a href={BOOK_LINK} target="_blank" rel="noreferrer">book on blockchain</a>. now i'm
            applying that same builder energy to AI/ML, agentic systems, and mobile products.
            <br /><br />
            <b>looking for:</b> summer 2026 roles in AI/ML, agentic AI, or fintech.
          </p>

          <p className="links-row">
            <a href="mailto:agarwak2@union.edu">say hi 👋</a>
            {' · '}
            <a href={TWITTER_LINK} target="_blank" rel="noreferrer">twitter</a>
            {' · '}
            <a href={GITHUB_LINK} target="_blank" rel="noreferrer">github</a>
            {' · '}
            <a href={LINKEDIN} target="_blank" rel="noreferrer">linkedin</a>
            {' · '}
            <a href="/resume.pdf" target="_blank" rel="noreferrer">resume ↓</a>
          </p>

          <hr />
        </div>

        <main>

          {/* CURRENTLY */}
          <section id="now">
            <p className="section-head">what i'm working on</p>
            {currentProjects.map((item) => (
              <p key={item.title} className="now-item">
                <span className="now-emoji">{item.emoji}</span>
                {' '}
                <b>{item.title}</b>
                {item.link
                  ? <> — {item.description} <a href={item.link} target="_blank" rel="noreferrer">{item.linkText || 'link →'}</a></>
                  : <> — {item.description}</>
                }
              </p>
            ))}
            <hr />
          </section>

          {/* ABOUT / JOURNEY */}
          <section id="journey">
            <h2 style={{ display: 'none' }}>Kshitij Agarwal's Journey</h2>
            <p className="section-head">about / journey</p>
            <p>
              i got hooked on decentralization in 2021 and just kept going. learned solidity, joined
              buildspace, got into chainshot's ethereum bootcamp (now alchemy university) while still
              under 18. led gen-z at buildspace (2,000+ teen devs, $10K treasury), helped run demo
              days for 500+ people, and shipped dApps at every ethglobal event i could get into. at
              eth san francisco, my team built alexandria and took 3rd place — with $250K+ in investor
              interest.
            </p>
            <p>
              that web3 foundation taught me how to build fast, ship publicly, and learn by doing.
              i've been applying it to everything since.
            </p>
            <p>
              now i'm a junior at union college (CS + economics, math minor, 3.91 GPA, drescher
              scholar). my time splits across a few things:
            </p>
            <ul className="journey-list">
              <li><b>AI/ML research</b> — building real-time EEG classification pipelines and adaptive neurofeedback systems in union's HCI lab. my senior thesis explores ZK proofs for verifiable AI inference.</li>
              <li><b>founding</b> — building Blue, and previously shipped Tariff (iOS, behavioral-friction monetization) and a multilingual AI voice agent for a cancer clinic in india.</li>
              <li><b>finance</b> — CIO of union's student investment fund ($1M+ AUM). previously risk manager.</li>
              <li><b>campus</b> — social chair at sigma phi fraternity. president of the robotics club.</li>
            </ul>
            <p>
              i wrote{' '}
              <a href={BOOK_LINK} target="_blank" rel="noreferrer">blockchain for business</a>
              {' '}(wings publications) to make crypto less intimidating. some stuff works out, some
              doesn't. i learn a lot either way.
            </p>
            <hr />
          </section>

          {/* PROJECTS */}
          <section id="projects">
            <h2 style={{ display: 'none' }}>Kshitij Agarwal's Projects</h2>
            <p className="section-head">projects</p>

            <p className="project-group-label">ai / ml / mobile</p>

            <div className="project">
              <b>image-to-latex model</b>
              <span className="project-tags">python · pytorch · resnet · lstm · attention</span>
              <br />
              end-to-end neural OCR system that converts images of math equations into LaTeX code.
              resnet-18 encoder extracts spatial features; LSTM decoder generates LaTeX via
              autoregressive decoding with attention. BLEU-4: 0.75+ (beat the original paper's
              baseline). token accuracy: ~55%. edit score: 0.85.
            </div>

            <div className="project">
              <b>real-time EEG pipeline</b>
              <span className="project-tags">python · scikit-learn · pytorch</span>
              <br />
              classifies emotional states from raw brainwave signals using SVM, CNN, and RNN models.
              integrated adaptive neurofeedback loop for dynamic mood regulation. built in union
              college's HCI lab.
            </div>

            <div className="project">
              <b>tariff</b>
              <span className="project-tags">swift · ios · screen time api</span>
              <br />
              iOS app that charges you real money (~$10) for opening addictive apps. texts a
              designated friend when you break a commitment. designed behavioral-friction monetization
              mechanics. pitched to founders inc canopy accelerator (SF).
            </div>

            <div className="project">
              <b>cancer clinic voice agent</b>
              <span className="project-tags">python · deepgram · exotel · google calendar · msg91</span>
              <br />
              multilingual AI voice agent for a cancer clinic in india. handles appointment scheduling
              in english, hindi, and bengali. integrates with clinic calendar and sends SMS
              confirmations.
            </div>

            <div className="project">
              <b>pdf-to-csv parser</b>
              <span className="project-tags">python · llms</span>
              <br />
              automated pipeline that parses unstructured charles schwab PDFs into structured CSVs.
              handles format variability with custom extraction logic and LLM-based fallback. reduced
              manual processing from hours to seconds.
            </div>

            <div className="project">
              <b>custom 8-bit CPU + assembler</b>
              <span className="project-tags">logisim · computer architecture</span>
              <br />
              designed a full 8-bit CPU from gate level: ALU with carry-lookahead and SLT, 8-register
              file, FSM-based control unit, complete instruction set. built a two-pass assembler that
              resolves labels and encodes instructions into machine code. full pipeline: .asm → binary
              → simulation.
            </div>

            <div className="project">
              <a href="https://github.com/omniscientHorizon/GreekLife-SentimentAnalysis" target="_blank" rel="noreferrer">
                <b>greek life sentiment analysis</b>
              </a>
              <span className="project-tags">python · nlp · vader</span>
              <br />
              analyzed 120+ years of union college newspaper archives with NLP. uncovered evolving
              sentiment and language patterns around greek life on campus.
            </div>

            <p className="project-group-label">web3</p>

            <div className="project">
              <a href="https://ethglobal.com/showcase/alexandria-3db0q" target="_blank" rel="noreferrer">
                <b>alexandria</b>
              </a>
              <span className="project-tags">solidity · web3 · 3rd place @ eth sf · $250k+ investor interest</span>
              <br />
              on-chain library with NFT-based ownership and automated author royalty splits via smart
              contracts. made it to level 2 @ alliance accelerators. shipped in a 48-hour build cycle.
            </div>

            <div className="project">
              <a href="https://ethglobal.com/showcase/b-trax-mi7o7" target="_blank" rel="noreferrer">
                <b>btrax</b>
              </a>
              <span className="project-tags">best audio @ ethglobal</span>
              <br />
              instrumental marketplace + community for producers. won best audio at ethglobal.
            </div>

            <div className="project">
              <a href="https://interviewninja.xyz" target="_blank" rel="noreferrer">
                <b>interview ninja</b>
              </a>
              <span className="project-tags">full-stack · edtech</span>
              <br />
              flashcards + timers for college application interviews. scaled to 1,000+ users across
              10+ countries.
            </div>

            <hr />
          </section>

          {/* TECH STACK */}
          <section id="stack" className="visually-hidden">
            <p className="section-head">tech stack</p>
            {STACK.map(({ label, tags }) => (
              <div key={label} className="stack-row">
                <span className="stack-label">{label}</span>
                <span className="stack-tags">
                  {tags.map((tag) => (
                    <span key={tag} className="stack-tag">{tag}</span>
                  ))}
                </span>
              </div>
            ))}
            <hr />
          </section>

          {/* RESEARCH */}
          <section id="research">
            <p className="section-head">research</p>
            <p>
              <b>real-time EEG emotion classification</b> — HCI Lab, Union College (Jan 2025 – present)
              <br />
              building classification pipeline using SVM, CNN, and RNN models on raw EEG signals.
              integrated adaptive neurofeedback loop for dynamic mood regulation based on model outputs.
            </p>
            <p>
              <b>economic accountability in U.S. gubernatorial elections</b> — Union College Summer Research Fellowship (Summer 2025)
              <br />
              investigated whether macroeconomic indicators predict U.S. gubernatorial election outcomes. built custom datasets, applied econometric methods, and analyzed how voters respond to economic conditions. mentored by Professor Prateek Arora. presented at the NY6 Undergraduate Research Conference. preparing for publication.
            </p>
            <p>
              <b>publication:</b>{' '}
              <a href={BOOK_LINK} target="_blank" rel="noreferrer">Blockchain for Business</a>
              {' '}— Wings Publications
            </p>
            <p>
              see more on{' '}
              <a href={LINKEDIN} target="_blank" rel="noreferrer">linkedin →</a>
            </p>
            <hr />
          </section>

        </main>
      </div>

      <footer>
        <b>kshitij agarwal</b>
        <br />
        <a href="mailto:agarwak2@union.edu">agarwak2@union.edu</a>
        {' · '}
        <a href={TWITTER_LINK} target="_blank" rel="noreferrer">twitter</a>
        {' · '}
        <a href={GITHUB_LINK} target="_blank" rel="noreferrer">github</a>
        {' · '}
        <a href={LINKEDIN} target="_blank" rel="noreferrer">linkedin</a>
        {' · '}
        <a href="/resume.pdf" target="_blank" rel="noreferrer">resume (pdf)</a>
        <br />
        ps: i respond to every email :)
      </footer>
    </div>
  );
}
